import ApiCollection            from '@widesk-core/models/ApiCollection';
import CodeKindModel            from '@/models/services/code/CodeKindModel';
import { CodeKindReference }    from '@/models/services/code/CodeKindModel';
import CodeServiceModel         from '@/models/CodeServiceModel';
import CodeTypeModel            from '@/models/services/code/CodeTypeModel';
import DeliverableExchangeModel from '@/models/services/shop/DeliverableExchangeModel';
import DeliverableModel         from '@/models/services/shop/DeliverableModel';
import FileModel                from '../shop/file/FileModel';
import { SupportedLanguage }    from '@/hooks/useTranslations';
import { computed }             from 'mobx';
import doc                      from '@widesk-core/decorators/doc';
import model                    from '@widesk-core/decorators/model';
import resolvableUrn            from '@widesk-core/decorators/resolvableUrn';
import { getIdFromUrn }         from '@widesk-core/tools/identifier';
import CodeStatusModel          from './CodeStatusModel';

@model.urnResource('code')
@doc.path('/codes/{?id}')
export default class CodeModel extends CodeServiceModel {
	private declare _tsFilters: {
		'codeKind': id;
		'codeKind.reference': CodeKindReference;
		'codeNetwork': id;
		'codeStatus.reference': Code_CodeStatusReference;
		'codeType.codeEntertainment.entertainmentUrn': Urn;
		'createdAt[after]': string;
		'createdAt[before]': string;
		'createdAt[strictly_after]': string;
		'createdAt[strictly_before]': string;
		'owner': id;
		'owner.ownerUrn': Urn;
		'updatedAt[after]': string | Date;
		'updatedAt[before]': string | Date;
		'updatedAt[strictly_after]': string | Date;
		'updatedAt[strictly_before]': string | Date;
		'value': id;
	};

	private declare _tsSorts: {
		'createdAt': string;
		'id': string;
		'updatedAt': string;
	};

	@doc.model(CodeKindModel) declare codeKind: CodeKindModel;
	@doc.model(CodeStatusModel) declare codeStatus: CodeStatusModel;
	@doc.model(CodeTypeModel) declare codeType: CodeTypeModel;
	@doc.string declare value: string;
	@doc.date declare createdAt: Dayjs;

	@resolvableUrn({ attributeName: 'sourceUrn' })
	declare source: DeliverableModel | DeliverableExchangeModel;

	@computed get sourceUrn() {
		return this.getUrn('source' as never);
	}

	@computed get isDeliverable() {
		return this.sourceUrn.includes(':deliverable:');
	}

	@computed get isDeliverableExchange() {
		return this.sourceUrn.includes(':deliverable_exchange:');
	}

	@computed get isGespass() {
		return this.value.startsWith('GPA/');
	}

	@computed get isFromShop() {
		return this.sourceUrn.includes(':shop:');
	}

	@computed get isDownloadable() {
		return this.isDeliverable || this.isDeliverableExchange;
	}

	/**
	 * Contremarque hydratée (avec son deliverable parent), renseignée par
	 * {@link CodeModel.loadDeliverables}. `undefined` pour un code adossé à un deliverable.
	 */
	public deliverableExchange?: DeliverableExchangeModel;

	/** Id du deliverable auquel ce code est rattaché. */
	@computed get deliverableId(): id | undefined {
		if (this.isDeliverable) return this.getId('source' as never);
		return this.deliverableExchange?.getId('deliverable' as never);
	}

	/** URN du deliverable rattaché à ce code. */
	@computed get deliverableUrn(): Urn | undefined {
		if (this.isDeliverable) return this.sourceUrn;
		return this.deliverableExchange?.getUrn('deliverable' as never);
	}

	/**
	 * Hydrate, en une seule requête, le deliverable parent des contremarques de la liste.
	 *
	 */
	public static async loadDeliverables(codes: CodeModel[]) {
		const exchangeCodes = codes.filter(code => code.isDeliverableExchange);
		if (!exchangeCodes.length) return;

		const exchanges = new ApiCollection(DeliverableExchangeModel);
		await exchanges
			.setFilter('id', exchangeCodes.map(code => getIdFromUrn(code.sourceUrn)))
			.list();

		exchangeCodes.forEach(code => {
			code.deliverableExchange = exchanges.models.find(
				exchange => String(exchange.id) === getIdFromUrn(code.sourceUrn),
			);
		});
	}

	public async download() {
		if (this.isDownloadable) {
			const collFile = new ApiCollection(FileModel);
			const reference = this.isDeliverable ? 'deliverable_pdf' : this.isDeliverableExchange ? 'deliverable_exchange_pdf' : undefined;
			if (reference) {
				await collFile
					.setFilter('typeSource.type.reference', [reference])
					.setFilter('typeSource.source.entityUrn', [this.getUrn('source' as any)])
					.list();
				const file = collFile.first();
				if (!file) throw new Error('noFile');
				await file.download(`${file.label}.pdf`);
			}
		}
	}

	public getTitle(language: SupportedLanguage) {
		let title = ''

		if (this.isGespass) {
			title = this.codeType.getTitle(language);
		} else {
			title = this.codeType.source.product.getTitle(language);

			if (this.codeType.source.product.multiVariant) {
				title+= ` - ${this.codeType.source.getTitle(language)}`;
			}
		}
		
		if (this.codeKind.reference === 'exchange') title += ` - ${this.codeKind.label}`;

		return title;
	}
}